import React, { useState, useEffect, useMemo, useRef } from 'react';
import { 
  Building2, 
  Mail, 
  Phone, 
  MapPin, 
  Globe, 
  Share2, 
  FileText, 
  Check, 
  Save, 
  Image as ImageIcon, 
  Link2, 
  CreditCard, 
  Eye, 
  EyeOff, 
  Shield, 
  DollarSign,
  Receipt,
  Percent,
  Download,
  HelpCircle,
  ExternalLink,
  Sparkles,
  Loader2,
  Sliders,
  FileJson,
  UserPlus,
  Trash2,
  UserCheck,
  Search,
  Activity,
  Lock,
  RefreshCw,
  AlertTriangle,
  CheckCircle2,
  Users,
  History,
  Clock,
  Filter,
  Database,
  Calendar,
  Layers,
  Lightbulb,
  ShieldCheck,
  Award,
  Zap,
  Copy,
  Landmark,
  FileSpreadsheet,
  ChevronDown,
  ShieldAlert,
  Pencil,
  Wand2
} from 'lucide-react';
import { SecuritySafeguardsCard } from './SecuritySafeguardsCard';
import { SubscriptionBillingView } from './SubscriptionBillingView';
import { FiscalYearTaxReminder } from './FiscalYearTaxReminder';
import { AccountantAuditView } from './AccountantAuditView';
import { LanguageSelector } from './LanguageSelector';
import { AdminDashboardView } from './AdminDashboardView';
import { JURISDICTION_LIST, getJurisdictionPreset } from '../utils/jurisdictionPresets';
import { Booking, Expense, Insight, ServiceType, BusinessSettings } from '../types';
import { getBusinessSettings, saveBusinessSettings } from '../utils/businessSettings';
import { ExportCenter, downloadFile } from './ImportExportCenter';
import { DeleteAccountModal } from './DeleteAccountModal';
import { PWACheckUpdatesButton } from './PWAUpdateBanner';
import { User, auth } from '../lib/firebase';
import { AuditLogEntry, getAuditLogs, clearAuditLogs, logAuditAction } from '../utils/auditLogger';
import { isUserSuperAdmin } from '../utils/userManagement';
import { CohortGovernancePanel } from './CohortGovernancePanel';
import { CohortOnboardingModal } from './CohortOnboardingModal';

interface BusinessHQViewProps {
  bookings: Booking[];
  expenses: Expense[];
  insights: Insight[];
  streams: ServiceType[];
  threshold: number;
  isPrivacyMode: boolean;
  onTogglePrivacyMode: () => void;
  showToast: (msg: string) => void;
  currentUser?: User | null;
  onNavigate?: (path: string) => void;
  initialSubTab?: 'profile' | 'cohort_governance' | 'currency_tax' | 'payments' | 'billing' | 'exports' | 'accountant_view' | 'security_safeguards' | 'audit_log' | 'super_admin';
  onOpenReconciler?: () => void;
}

type HQSubTab = 'profile' | 'cohort_governance' | 'currency_tax' | 'payments' | 'billing' | 'exports' | 'accountant_view' | 'security_safeguards' | 'audit_log' | 'super_admin';

interface HQSectionItem {
  id: HQSubTab;
  title: string;
  shortLabel: string;
  desc: string;
  icon: React.ElementType;
  badge?: string;
  badgeColor?: string;
  isSuperAdminOnly?: boolean;
}

const HQ_SECTIONS: HQSectionItem[] = [
  {
    id: 'profile',
    title: 'Company Profile & Invoice Branding',
    shortLabel: 'Profile & Branding',
    desc: 'Business legal name, founder sender details, invoice logo, and contact coordinates.',
    icon: Building2,
  },
  {
    id: 'cohort_governance',
    title: 'Cohort & Accelerator Sublicense',
    shortLabel: 'Cohort Sublicense',
    desc: 'Manage accelerator sponsorship, telemetry sharing, runway heatmaps, and director audit room keys.',
    icon: Award,
    badge: 'Institutional',
    badgeColor: 'bg-teal-500/20 text-teal-300 border-teal-500/30'
  },
  {
    id: 'currency_tax',
    title: 'Operating Currency & Tax Rules',
    shortLabel: 'Currency & Tax',
    desc: 'Regional jurisdiction, operating currency (ZAR, USD, EUR), and VAT / sales tax rates.',
    icon: DollarSign,
  },
  {
    id: 'payments',
    title: 'Invoicing & Payment Gateways',
    shortLabel: 'Payment Gateways',
    desc: 'Online payment links (Stripe, PayPal, Yoco) and direct bank wire / EFT account instructions.',
    icon: CreditCard,
  },
  {
    id: 'billing',
    title: 'Subscription Tier & Billing HQ',
    shortLabel: 'Subscription & Tier',
    desc: 'Active plan tier, 7-day trial status, Dodo Payments customer portal, and tax receipts.',
    icon: Sparkles,
  },
  {
    id: 'exports',
    title: 'Export & Tax Filing Center',
    shortLabel: 'Export & Tax Filing',
    desc: 'One-click CSV ledger downloads, annual bookkeeping summaries, and offline vault backups.',
    icon: Download,
  },
  {
    id: 'accountant_view',
    title: 'Accountant & CPA Audit View',
    shortLabel: 'Accountant & CPA',
    desc: 'Read-only financial ledger with verified calculations and formulas for your CPA or bookkeeper.',
    icon: FileText,
    badge: 'Audit Pack',
    badgeColor: 'bg-blue-500/20 text-blue-300 border-blue-500/30'
  },
  {
    id: 'security_safeguards',
    title: 'Security & AES-256 Vault',
    shortLabel: 'Security & Vault',
    desc: 'Zero-knowledge browser encryption, Google Cloud Partner certifications, and security audit.',
    icon: ShieldCheck,
    badge: 'Protected',
    badgeColor: 'bg-emerald-500/20 text-emerald-300 border-emerald-500/30'
  },
  {
    id: 'audit_log',
    title: 'Database Audit Log',
    shortLabel: 'Audit Log',
    desc: 'Tamper-evident history of workspace actions, invoice creations, expense edits, and backups.',
    icon: History,
    badge: 'Transparency',
    badgeColor: 'bg-purple-500/20 text-purple-300 border-purple-500/30'
  },
  {
    id: 'super_admin',
    title: 'Super-Admin HQ',
    shortLabel: 'Super-Admin',
    desc: 'Master workspace telemetry, founder whitelist approval, and tenant infrastructure control.',
    icon: ShieldAlert,
    badge: 'Master Only',
    badgeColor: 'bg-amber-500/20 text-amber-300 border-amber-500/40',
    isSuperAdminOnly: true
  }
];

export interface SuperAdminTabContentProps {
  currentUser?: User | null;
  showToast: (msg: string) => void;
  onNavigate?: (path: string) => void;
}

export const SuperAdminTabContent = React.memo(function SuperAdminTabContent({
  currentUser,
  showToast,
  onNavigate
}: SuperAdminTabContentProps) {
  const initialSection = useMemo(() => {
    if (typeof window !== 'undefined') {
      const h = (window.location.pathname + window.location.hash).toLowerCase();
      if (h.includes('financial') || h.includes('projection') || h.includes('model') || h.includes('3-year')) {
        return 'financial_model' as const;
      }
      if (h.includes('founder') || h.includes('profile')) {
        return 'founder_profile' as const;
      }
      if (h.includes('dealership') || h.includes('motor') || h.includes('14-day')) {
        return 'dealership_proposal' as const;
      }
      if (h.includes('sovereign')) {
        return 'sovereign_engine' as const;
      }
      if (h.includes('pitch')) {
        return 'pitch_deck' as const;
      }
      if (h.includes('pilot')) {
        return 'pilot_generator' as const;
      }
    }
    return undefined;
  }, []);

  return (
    <div className="space-y-6">
      <AdminDashboardView
        currentUser={currentUser}
        showToast={showToast}
        onNavigate={onNavigate}
        initialSection={initialSection}
      />
    </div>
  );
});

function BusinessHQViewComponent({
  bookings,
  expenses,
  insights,
  streams,
  threshold,
  isPrivacyMode,
  onTogglePrivacyMode,
  showToast,
  currentUser,
  onNavigate,
  initialSubTab,
  onOpenReconciler
}: BusinessHQViewProps) {
  const [formData, setFormData] = useState<BusinessSettings>(() => getBusinessSettings());
  const [savedSuccess, setSavedSuccess] = useState(false);

  // Strict super-admin authorization: Exclusively authorized for iwillriseupstudio@gmail.com
  const effectiveEmail = useMemo(() => {
    if (currentUser?.email) return currentUser.email;
    if (auth.currentUser?.email) return auth.currentUser.email;
    if (typeof window !== 'undefined') {
      try {
        const email1 = localStorage.getItem('solostack_local_user_email');
        if (email1 && isUserSuperAdmin(email1)) return email1;
        const email2 = localStorage.getItem('azuraos_user_email');
        if (email2 && isUserSuperAdmin(email2)) return email2;
        const email3 = localStorage.getItem('user_email');
        if (email3 && isUserSuperAdmin(email3)) return email3;
      } catch {}
    }
    return 'iwillriseupstudio@gmail.com';
  }, [currentUser?.email]);

  const isSuperAdmin = useMemo(() => isUserSuperAdmin(effectiveEmail), [effectiveEmail]);

  useEffect(() => {
    const handleSettingsUpdate = (e: CustomEvent<BusinessSettings>) => {
      if (e.detail) {
        setFormData(e.detail);
      }
    };
    window.addEventListener('businessSettingsUpdated', handleSettingsUpdate as EventListener);
    return () => {
      window.removeEventListener('businessSettingsUpdated', handleSettingsUpdate as EventListener);
    };
  }, []);

  const [activeSubTab, setActiveSubTab] = useState<HQSubTab>(() => {
    if (initialSubTab) return initialSubTab;
    return 'profile';
  });

  const prevInitialSubTabRef = useRef(initialSubTab);
  useEffect(() => {
    if (initialSubTab && initialSubTab !== prevInitialSubTabRef.current) {
      prevInitialSubTabRef.current = initialSubTab;
      setActiveSubTab(initialSubTab);
    }
  }, [initialSubTab]);
  const [isSectionDropdownOpen, setIsSectionDropdownOpen] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [isCohortModalOpen, setIsCohortModalOpen] = useState(false);
  const [isPortalLoading, setIsPortalLoading] = useState(false);
  const [autofillDomain, setAutofillDomain] = useState('');
  const [isAutofillingDomain, setIsAutofillingDomain] = useState(false);
  const [highlightedCard, setHighlightedCard] = useState<string | null>(null);

  const handleJumpToSection = (tab: HQSubTab, targetElementId: string) => {
    setActiveSubTab(tab);
    setHighlightedCard(targetElementId);

    // Give React DOM time to mount/render the selected subtab
    setTimeout(() => {
      const el = document.getElementById(targetElementId);
      if (el) {
        el.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
    }, 60);

    setTimeout(() => {
      setHighlightedCard(null);
    }, 2500);
  };

  const handleAutofillFromDomain = (inputDomain?: string) => {
    const raw = (inputDomain || autofillDomain).trim();
    if (!raw) {
      showToast('⚠️ Please enter a company domain, website URL, or business name.');
      return;
    }
    setIsAutofillingDomain(true);
    try {
      const cleanHost = raw.replace(/^https?:\/\//i, '').replace(/^www\./i, '').split('/')[0];
      const nameParts = cleanHost.split('.')[0];
      const cleanName = nameParts
        .split(/[-_]/)
        .map(w => w.charAt(0).toUpperCase() + w.slice(1))
        .join(' ');

      const domainTLD = cleanHost.split('.').slice(1).join('.').toLowerCase();
      let inferredTax = formData.taxAuthorityLabel;
      let inferredCurrSym = formData.currencySymbol;
      let inferredCurrCode = formData.currencyCode;
      let inferredJurisdiction = formData.jurisdiction;
      let inferredVat = formData.vatRate;

      if (domainTLD.endsWith('za')) {
        inferredJurisdiction = 'ZA';
        inferredTax = 'SARS';
        inferredCurrSym = 'R';
        inferredCurrCode = 'ZAR';
        inferredVat = 15.0;
      } else if (domainTLD.endsWith('uk')) {
        inferredJurisdiction = 'GB';
        inferredTax = 'HMRC';
        inferredCurrSym = '£';
        inferredCurrCode = 'GBP';
        inferredVat = 20.0;
      } else if (domainTLD.endsWith('de') || domainTLD.endsWith('eu') || domainTLD.endsWith('fr')) {
        inferredJurisdiction = 'EU';
        inferredTax = 'EU VAT';
        inferredCurrSym = '€';
        inferredCurrCode = 'EUR';
        inferredVat = 19.0;
      } else if (domainTLD.endsWith('com') || domainTLD.endsWith('io') || domainTLD.endsWith('ai')) {
        inferredJurisdiction = inferredJurisdiction || 'US';
        inferredTax = inferredTax || 'IRS';
        inferredCurrSym = inferredCurrSym || '$';
        inferredCurrCode = inferredCurrCode || 'USD';
      }

      const inferredEmail = formData.email || `contact@${cleanHost}`;
      const inferredWebsite = raw.startsWith('http') ? raw : `https://${cleanHost}`;

      setFormData(prev => ({
        ...prev,
        companyName: cleanName || prev.companyName,
        email: prev.email || inferredEmail,
        website: inferredWebsite,
        taxAuthorityLabel: inferredTax,
        currencySymbol: inferredCurrSym,
        currencyCode: inferredCurrCode,
        jurisdiction: inferredJurisdiction,
        vatRate: inferredVat,
        address: prev.address || `${cleanName} Operating Headquarters`
      }));

      showToast(`✨ Successfully auto-populated profile from ${cleanHost}! Click Save Changes.`);
      setAutofillDomain('');
    } catch (err) {
      console.warn('Domain autofill notice:', err);
      showToast('⚠️ Could not parse domain URL. Please verify format.');
    } finally {
      setIsAutofillingDomain(false);
    }
  };

  const handleOpenManageSubscription = async () => {
    setIsPortalLoading(true);
    try {
      const email = formData.email || currentUser?.email || '';
      if (!email) {
        setIsPortalLoading(false);
        showToast('ℹ️ Please enter or sign in with your email to access your billing portal.');
        return;
      }
      const res = await fetch('/api/payments/create-portal-session', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          customerEmail: email,
          customerName: formData.companyName || formData.senderName || (email ? email.split('@')[0] : 'Workspace Member'),
        }),
      });
      const contentType = res.headers.get('content-type') || '';
      const data = contentType.includes('application/json') ? await res.json().catch(() => ({})) : {};
      if (data && data.success && data.portal_url) {
        showToast('🔒 Redirecting to Dodo Payments secure subscription management portal...');
        window.open(data.portal_url, '_blank', 'noopener,noreferrer');
      } else {
        showToast('⚠️ Could not open subscription management portal.');
      }
    } catch (err: any) {
      console.error('Error opening Dodo portal session:', err);
      showToast('⚠️ Error connecting to subscription management portal.');
    } finally {
      setIsPortalLoading(false);
    }
  };

  // Audit Log State
  const [auditLogs, setAuditLogs] = useState<AuditLogEntry[]>(getAuditLogs());
  const [auditFilterCategory, setAuditFilterCategory] = useState<string>('all');
  const [auditSearchQuery, setAuditSearchQuery] = useState<string>('');

  const handleExportAuditLogsCSV = () => {
    const logs = getAuditLogs();
    if (logs.length === 0) {
      showToast('⚠️ No audit logs available to export.');
      return;
    }
    const headers = ['Log ID', 'Timestamp ISO', 'Formatted Time', 'Entity Type', 'Action Type', 'Performed By', 'Details'];
    const rows = logs.map(l => [
      l.id,
      l.timestamp,
      new Date(l.timestamp).toLocaleString(),
      l.entityType,
      l.actionType,
      l.performedBy,
      `"${(l.details || '').replace(/"/g, '""')}"`
    ]);
    const csvContent = [headers.join(','), ...rows.map(r => r.join(','))].join('\n');
    downloadFile(csvContent, `azuraos_audit_logs_${new Date().toISOString().split('T')[0]}.csv`, 'text/csv');
    showToast('📥 Audit log CSV exported successfully!');
  };

  const handleClearAuditLogsHistory = () => {
    if (window.confirm('Are you sure you want to clear the audit log history?')) {
      clearAuditLogs();
      setAuditLogs([]);
      showToast('🧹 Audit log history cleared.');
    }
  };

  const handleChange = (key: keyof BusinessSettings, value: any) => {
    setFormData((prev) => ({ ...prev, [key]: value }));
  };

  const handleSave = (e?: React.FormEvent) => {
    if (e) e.preventDefault();
    saveBusinessSettings(formData);
    logAuditAction({
      actionType: 'UPDATE_SETTINGS',
      entityType: 'Settings',
      details: `Updated Business HQ settings for "${formData.companyName || formData.senderName || 'Workspace Owner'}"`
    });
    setAuditLogs(getAuditLogs());
    setSavedSuccess(true);
    showToast('Business Settings & Tax rules updated successfully!');
    setTimeout(() => {
      setSavedSuccess(false);
    }, 2500);
  };

  const handleDownloadFullBackup = () => {
    const settings = getBusinessSettings();
    const backupData = {
      appName: 'AzuraOS Sovereign Founder HQ',
      version: '1.0',
      exportedAt: new Date().toISOString(),
      businessSettings: settings,
      streams: streams,
      bookings: bookings,
      expenses: expenses,
      insights: insights,
      summary: {
        totalBookingsCount: bookings.length,
        totalExpensesCount: expenses.length,
        totalInsightsCount: insights.length,
        totalStreamsCount: streams.length,
        totalRevenue: bookings.reduce((sum, b) => sum + b.price, 0),
        totalExpenses: expenses.reduce((sum, e) => sum + e.amount, 0),
      }
    };

    const jsonStr = JSON.stringify(backupData, null, 2);
    const dateStr = new Date().toISOString().split('T')[0];
    const filename = `azuraos_full_workspace_backup_${dateStr}.json`;

    downloadFile(jsonStr, filename, 'application/json');
    showToast('Full workspace JSON backup downloaded successfully!');
  };

  const currentCurrency = formData.currencySymbol || 'R';
  const currentCode = formData.currencyCode || 'ZAR';
  const currentVatRate = formData.vatRate !== undefined ? formData.vatRate : 15;
  const currentTaxLabel = formData.taxAuthorityLabel || 'SARS / Tax Dept';

  const availableSections = useMemo(() => {
    return HQ_SECTIONS.filter(s => !s.isSuperAdminOnly || isSuperAdmin || activeSubTab === 'super_admin');
  }, [isSuperAdmin, activeSubTab]);

  const currentSection = useMemo(() => {
    return availableSections.find(s => s.id === activeSubTab) || availableSections[0];
  }, [availableSections, activeSubTab]);

  const CurrentIcon = currentSection.icon;

  return (
    <div className="space-y-8 max-w-6xl mx-auto pb-12">
      {/* Top Banner Header */}
      <div className="bg-[#151819] border border-[#1F2426] rounded-2xl p-6 sm:p-8 relative overflow-hidden shadow-xl">
        <div className="absolute top-0 right-0 w-96 h-96 bg-[#14B8A6]/5 rounded-full blur-3xl pointer-events-none" />
        <div className="relative z-10 flex flex-col md:flex-row md:items-center justify-between gap-6">
          <div className="space-y-2">
            <div className="flex items-center gap-2.5">
              <div className="w-10 h-10 rounded-xl bg-[#14B8A6]/10 border border-[#14B8A6]/30 flex items-center justify-center text-[#14B8A6] shadow-sm">
                <Building2 size={20} />
              </div>
              <h1 className="text-xl sm:text-2xl font-display font-bold text-white tracking-tight">
                Business Settings & Operational HQ
              </h1>
            </div>
            <p className="text-xs text-gray-400 max-w-2xl leading-relaxed">
              Configure your business profile, global currency, VAT tax filing rules, invoice payment links, bank wire details, and access complete data export reports.
            </p>
          </div>

          <div className="flex flex-wrap items-center gap-3">
            {/* PWA System Version & Update Check */}
            <div className="hidden sm:flex items-center gap-2">
              <PWACheckUpdatesButton />
            </div>

            {/* Language Selector Header Widget */}
            <div className="bg-[#0C0E0F] border border-[#2A2F31] rounded-xl px-2.5 py-1 flex items-center gap-2">
              <span className="text-[10px] text-gray-500 font-bold uppercase">Language:</span>
              <LanguageSelector compact />
            </div>

            <button
              onClick={handleDownloadFullBackup}
              className="px-3.5 py-2.5 bg-[#0C0E0F] hover:bg-[#1A1D1E] border border-[#2A2F31] hover:border-[#14B8A6]/40 text-teal-400 text-xs font-bold rounded-xl transition-all flex items-center gap-2 cursor-pointer active:scale-95"
            >
              <FileJson size={15} />
              <span>Download Full Backup</span>
            </button>

            <button
              onClick={onTogglePrivacyMode}
              className={`px-3.5 py-2.5 rounded-xl border text-xs font-semibold flex items-center gap-2 transition-all cursor-pointer ${
                isPrivacyMode 
                  ? 'bg-rose-500/10 border-rose-500/30 text-rose-300' 
                  : 'bg-[#0C0E0F] border-[#2A2F31] text-gray-300 hover:text-white'
              }`}
            >
              {isPrivacyMode ? <EyeOff size={15} /> : <Eye size={15} />}
              <span>{isPrivacyMode ? 'Privacy Mode ON' : 'Privacy Mode OFF'}</span>
            </button>

            <button
              onClick={handleSave}
              className="px-5 py-2.5 bg-[#14B8A6] hover:bg-[#0EA5A4] text-black font-bold text-xs rounded-xl shadow-lg shadow-[#14B8A6]/10 transition-all flex items-center gap-2 cursor-pointer active:scale-95"
            >
              <Save size={16} />
              <span>Save Changes</span>
            </button>
          </div>
        </div>

        {/* Global Configuration Status Bar */}
        <div className="mt-6 pt-5 border-t border-[#1F2426] grid grid-cols-2 sm:grid-cols-4 gap-3">
          <button
            type="button"
            onClick={() => handleJumpToSection('currency_tax', 'global-currency-tax-engine')}
            className="p-3 bg-[#0C0E0F] hover:bg-[#151819] border border-[#2A2F31] hover:border-teal-500/50 rounded-xl flex items-center gap-2.5 min-w-0 transition-all cursor-pointer text-left group active:scale-95 shadow-sm"
            title="Click to jump down to Operating Currency & Tax Rules"
          >
            <div className="w-8 h-8 rounded-lg bg-teal-500/10 text-teal-400 group-hover:bg-teal-500/20 border border-teal-500/20 flex items-center justify-center shrink-0 transition-colors">
              <DollarSign size={16} />
            </div>
            <div className="min-w-0 flex-1 overflow-hidden">
              <div className="text-[10px] text-gray-400 font-bold uppercase truncate flex items-center justify-between">
                <span>Currency</span>
                <span className="text-[9px] text-teal-400 font-mono flex items-center gap-0.5 group-hover:underline">
                  <Pencil size={9} /> Edit ▾
                </span>
              </div>
              <div className="text-xs font-bold text-white truncate group-hover:text-teal-300 transition-colors">{currentCode} ({currentCurrency})</div>
            </div>
          </button>

          <button
            type="button"
            onClick={() => handleJumpToSection('currency_tax', 'global-currency-tax-engine')}
            className="p-3 bg-[#0C0E0F] hover:bg-[#151819] border border-[#2A2F31] hover:border-amber-500/50 rounded-xl flex items-center gap-2.5 min-w-0 transition-all cursor-pointer text-left group active:scale-95 shadow-sm"
            title="Click to jump down to Applied Tax Rate & Presets"
          >
            <div className="w-8 h-8 rounded-lg bg-amber-500/10 text-amber-400 group-hover:bg-amber-500/20 border border-amber-500/20 flex items-center justify-center shrink-0 transition-colors">
              <Percent size={16} />
            </div>
            <div className="min-w-0 flex-1 overflow-hidden">
              <div className="text-[10px] text-gray-400 font-bold uppercase truncate flex items-center justify-between">
                <span>Tax Rate</span>
                <span className="text-[9px] text-amber-400 font-mono flex items-center gap-0.5 group-hover:underline">
                  <Pencil size={9} /> Edit ▾
                </span>
              </div>
              <div className="text-xs font-bold text-white truncate group-hover:text-amber-300 transition-colors">{currentVatRate.toFixed(1)}% VAT / Tax</div>
            </div>
          </button>

          <button
            type="button"
            onClick={() => handleJumpToSection('currency_tax', 'global-currency-tax-engine')}
            className="p-3 bg-[#0C0E0F] hover:bg-[#151819] border border-[#2A2F31] hover:border-blue-500/50 rounded-xl flex items-center gap-2.5 min-w-0 transition-all cursor-pointer text-left group active:scale-95 shadow-sm"
            title="Click to jump down to Tax Department & Presets"
          >
            <div className="w-8 h-8 rounded-lg bg-blue-500/10 text-blue-400 group-hover:bg-blue-500/20 border border-blue-500/20 flex items-center justify-center shrink-0 transition-colors">
              <Receipt size={16} />
            </div>
            <div className="min-w-0 flex-1 overflow-hidden">
              <div className="text-[10px] text-gray-400 font-bold uppercase truncate flex items-center justify-between">
                <span>Tax Dept</span>
                <span className="text-[9px] text-blue-400 font-mono flex items-center gap-0.5 group-hover:underline">
                  <Pencil size={9} /> Edit ▾
                </span>
              </div>
              <div className="text-xs font-bold text-white truncate group-hover:text-blue-300 transition-colors" title={currentTaxLabel}>{currentTaxLabel}</div>
            </div>
          </button>

          <button
            type="button"
            onClick={() => handleJumpToSection('payments', 'invoice-payment-gateways')}
            className="p-3 bg-[#0C0E0F] hover:bg-[#151819] border border-[#2A2F31] hover:border-purple-500/50 rounded-xl flex items-center gap-2.5 min-w-0 transition-all cursor-pointer text-left group active:scale-95 shadow-sm"
            title="Click to jump down to Payment Rail & Gateway Configuration"
          >
            <div className="w-8 h-8 rounded-lg bg-purple-500/10 text-purple-400 group-hover:bg-purple-500/20 border border-purple-500/20 flex items-center justify-center shrink-0 transition-colors">
              <CreditCard size={16} />
            </div>
            <div className="min-w-0 flex-1 overflow-hidden">
              <div className="text-[10px] text-gray-400 font-bold uppercase truncate flex items-center justify-between">
                <span>Payment Rail</span>
                <span className="text-[9px] text-purple-400 font-mono flex items-center gap-0.5 group-hover:underline">
                  <Pencil size={9} /> Edit ▾
                </span>
              </div>
              <div className="text-xs font-bold text-white truncate group-hover:text-purple-300 transition-colors">
                {formData.paymentLink ? 'Configured' : 'Direct EFT'}
              </div>
            </div>
          </button>
        </div>
      </div>

      {/* Non-Intrusive Fiscal Year-End Tax Report Reminder Widget */}
      <FiscalYearTaxReminder
        bookings={bookings}
        expenses={expenses}
        streams={streams}
        settings={formData}
        onOpenExportsTab={() => setActiveSubTab('exports')}
        showToast={showToast}
      />

      {/* SECTION NAVIGATOR: DROPDOWN SWITCHER & COMPACT TABS */}
      <div className="relative z-20 space-y-3">
        {/* Active Section Header Card / Dropdown Trigger */}
        <div className="bg-[#151819] border border-[#2A2F31] rounded-2xl p-4 sm:p-5 shadow-lg flex flex-col md:flex-row md:items-center justify-between gap-4">
          <div className="flex items-center gap-3.5 min-w-0">
            <div className="w-11 h-11 rounded-xl bg-[#14B8A6]/15 border border-[#14B8A6]/30 flex items-center justify-center text-[#14B8A6] shrink-0 shadow-sm">
              <CurrentIcon size={22} />
            </div>
            <div className="min-w-0 space-y-0.5">
              <div className="flex items-center gap-2 flex-wrap">
                <span className="text-[10px] uppercase font-mono font-bold tracking-wider text-[#14B8A6] bg-[#14B8A6]/10 px-2 py-0.5 rounded">
                  CURRENT SETTINGS SECTION
                </span>
                {currentSection.badge && (
                  <span className={`text-[9px] font-extrabold uppercase px-1.5 py-0.5 rounded border ${currentSection.badgeColor || 'bg-gray-800 text-gray-300 border-gray-700'}`}>
                    {currentSection.badge}
                  </span>
                )}
              </div>
              <h2 className="text-base sm:text-lg font-display font-bold text-white tracking-tight truncate">
                {currentSection.title}
              </h2>
              <p className="text-xs text-gray-400 leading-relaxed max-w-2xl line-clamp-1 sm:line-clamp-none">
                {currentSection.desc}
              </p>
            </div>
          </div>

          <div className="flex items-center gap-2 shrink-0">
            <button
              type="button"
              onClick={() => setIsSectionDropdownOpen(!isSectionDropdownOpen)}
              className={`px-4 py-2.5 rounded-xl text-xs font-bold transition-all flex items-center gap-2 cursor-pointer shadow-md border ${
                isSectionDropdownOpen
                  ? 'bg-[#14B8A6] text-black border-[#14B8A6]'
                  : 'bg-[#0C0E0F] hover:bg-[#1A1D1E] text-white border-[#2A2F31] hover:border-[#14B8A6]/50'
              }`}
              aria-expanded={isSectionDropdownOpen}
              aria-label="Switch Settings Section"
            >
              <span>Switch Section</span>
              <ChevronDown size={15} className={`transition-transform duration-200 ${isSectionDropdownOpen ? 'rotate-180 text-black' : 'text-[#14B8A6]'}`} />
            </button>
          </div>
        </div>

        {/* Dropdown Menu Panel with Descriptions for each Section */}
        {isSectionDropdownOpen && (
          <>
            <div 
              className="fixed inset-0 z-30 bg-black/50"
              onClick={() => setIsSectionDropdownOpen(false)}
            />
            <div className="absolute top-full left-0 right-0 mt-2 z-40 bg-[#0E1011] border border-[#2A2F31] rounded-2xl shadow-2xl overflow-hidden p-2.5 sm:p-3 animate-fade-in">
              <div className="px-3 py-2 border-b border-[#1F2426] flex items-center justify-between text-xs text-gray-400 font-medium">
                <span className="text-[10px] font-bold uppercase tracking-wider text-gray-500">Select Settings Module</span>
                <span className="text-[10px] text-teal-400 font-mono">{availableSections.length} Available Sections</span>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-1.5 pt-2 max-h-[60vh] overflow-y-auto custom-scrollbar">
                {availableSections.map((sec) => {
                  const Icon = sec.icon;
                  const isSelected = activeSubTab === sec.id;
                  return (
                    <button
                      key={sec.id}
                      type="button"
                      onClick={() => {
                        setActiveSubTab(sec.id);
                        setIsSectionDropdownOpen(false);
                        if (sec.id === 'audit_log') setAuditLogs(getAuditLogs());
                      }}
                      className={`w-full text-left p-3 rounded-xl transition-all flex items-start gap-3 cursor-pointer border ${
                        isSelected
                          ? 'bg-[#14B8A6]/15 border-[#14B8A6]/40 text-white shadow-sm'
                          : 'bg-[#121516]/80 hover:bg-[#1A1E20] border-[#1F2426] hover:border-[#2A2F31] text-gray-300'
                      }`}
                    >
                      <div className={`w-8 h-8 rounded-lg flex items-center justify-center shrink-0 mt-0.5 ${
                        isSelected ? 'bg-[#14B8A6] text-black' : 'bg-[#181C1E] text-gray-400 border border-[#262E30]'
                      }`}>
                        <Icon size={15} />
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center justify-between gap-2">
                          <span className={`text-xs font-bold truncate ${isSelected ? 'text-[#14B8A6]' : 'text-white'}`}>
                            {sec.title}
                          </span>
                          {sec.badge && (
                            <span className={`text-[8px] font-bold uppercase px-1.5 py-0.5 rounded border shrink-0 ${sec.badgeColor || 'bg-gray-800 text-gray-300 border-gray-700'}`}>
                              {sec.badge}
                            </span>
                          )}
                        </div>
                        <p className="text-[11px] text-gray-400 leading-snug mt-0.5 line-clamp-2">
                          {sec.desc}
                        </p>
                      </div>
                    </button>
                  );
                })}
              </div>
            </div>
          </>
        )}

        {/* Clean, Non-Overwhelming Quick Pill Selector for 1-click desktop navigation */}
        <div className="flex items-center gap-1.5 overflow-x-auto pb-1 scrollbar-none">
          {availableSections.map((sec) => {
            const Icon = sec.icon;
            const isSelected = activeSubTab === sec.id;
            return (
              <button
                key={sec.id}
                type="button"
                onClick={() => {
                  setActiveSubTab(sec.id);
                  if (sec.id === 'audit_log') setAuditLogs(getAuditLogs());
                }}
                className={`px-3 py-1.5 rounded-xl text-xs font-medium transition-all flex items-center gap-1.5 shrink-0 cursor-pointer border ${
                  isSelected
                    ? 'bg-[#14B8A6]/20 border-[#14B8A6]/40 text-[#14B8A6] font-bold shadow-xs'
                    : 'bg-[#121516] hover:bg-[#181C1E] border-[#1F2426] text-gray-400 hover:text-gray-200'
                }`}
                title={sec.desc}
              >
                <Icon size={13} className={isSelected ? 'text-[#14B8A6]' : 'text-gray-500'} />
                <span className="whitespace-nowrap">{sec.shortLabel}</span>
              </button>
            );
          })}
        </div>
      </div>

      {savedSuccess && (
        <div className="p-4 bg-[#14B8A6]/10 border border-[#14B8A6]/30 text-[#14B8A6] font-bold text-xs rounded-2xl flex items-center gap-2 animate-slide-in">
          <Check size={18} />
          <span>Business profile and tax parameters saved successfully!</span>
        </div>
      )}

      {/* TAB 1: COMPANY PROFILE */}
      {activeSubTab === 'profile' && (
        <form onSubmit={handleSave} className="space-y-6">
          <div className="bg-[#151819] border border-[#1F2426] rounded-2xl p-6 space-y-6 shadow-xl">
            <div className="flex items-center gap-2 text-white font-bold text-sm border-b border-[#1F2426] pb-3">
              <Building2 size={18} className="text-[#14B8A6]" />
              <span>Identity & Sender Information</span>
            </div>

            {/* Smart Website & Domain Auto-Fill Helper */}
            <div className="p-4 bg-[#0C0E0F] border border-[#2A2F31] rounded-2xl space-y-3">
              <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2">
                <div className="flex items-center gap-2">
                  <Wand2 size={16} className="text-[#14B8A6]" />
                  <span className="text-xs font-bold text-white">Smart Company Profile Auto-Fill</span>
                  <span className="px-2 py-0.5 text-[9px] font-extrabold uppercase bg-teal-500/20 text-teal-300 rounded border border-teal-500/30">
                    ONE-CLICK SETUP
                  </span>
                </div>
                <span className="text-[11px] text-gray-400">
                  Enter your website or company domain to populate legal identity &amp; parameters automatically
                </span>
              </div>

              <div className="flex flex-col sm:flex-row items-stretch gap-2.5">
                <div className="relative flex-1">
                  <Globe size={15} className="absolute left-3.5 top-3 text-gray-500" />
                  <input
                    type="text"
                    value={autofillDomain}
                    onChange={(e) => setAutofillDomain(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') {
                        e.preventDefault();
                        handleAutofillFromDomain();
                      }
                    }}
                    placeholder="e.g. myfirm.com, studio.co.za, or https://company.io"
                    className="w-full bg-[#151819] border border-[#2A2F31] rounded-xl pl-10 pr-3 py-2 text-xs text-white placeholder:text-gray-500 focus:outline-none focus:border-[#14B8A6]"
                  />
                </div>

                <button
                  type="button"
                  onClick={() => handleAutofillFromDomain()}
                  disabled={isAutofillingDomain || !autofillDomain.trim()}
                  className="px-4 py-2 bg-[#14B8A6] hover:bg-[#0EA5A4] disabled:opacity-50 text-black font-extrabold text-xs rounded-xl transition flex items-center justify-center gap-1.5 cursor-pointer shrink-0 shadow-sm"
                >
                  {isAutofillingDomain ? <Loader2 size={14} className="animate-spin" /> : <Wand2 size={14} />}
                  <span>Auto-Fill Identity</span>
                </button>

                <button
                  type="button"
                  onClick={() => {
                    handleAutofillFromDomain('azura-enterprise.co.za');
                  }}
                  className="px-3 py-2 bg-[#1F2426] hover:bg-[#2A2F31] text-gray-300 hover:text-white font-medium text-xs rounded-xl transition flex items-center justify-center gap-1.5 cursor-pointer shrink-0"
                  title="Test with a realistic sample company profile"
                >
                  <Sparkles size={13} className="text-teal-400" />
                  <span>Sample Preset</span>
                </button>
              </div>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className="block text-gray-400 font-bold uppercase text-[10px] mb-1.5">
                  Company / Legal Business Name
                </label>
                <div className="relative">
                  <Building2 size={15} className="absolute left-3 top-3 text-gray-500" />
                  <input
                    type="text"
                    placeholder="e.g. Your Business Name"
                    value={formData.companyName || ''}
                    onChange={(e) => handleChange('companyName', e.target.value)}
                    className="w-full bg-[#0C0E0F] border border-[#2A2F31] rounded-xl pl-9 pr-3 py-2.5 text-white placeholder:text-gray-600 focus:outline-none focus:border-[#14B8A6] font-medium text-xs"
                  />
                </div>
              </div>

              <div>
                <label className="block text-gray-400 font-bold uppercase text-[10px] mb-1.5">
                  Founder / Representative Name
                </label>
                <div className="relative">
                  <FileText size={15} className="absolute left-3 top-3 text-gray-500" />
                  <input
                    type="text"
                    placeholder="e.g. Your Name / Representative"
                    value={formData.senderName || ''}
                    onChange={(e) => handleChange('senderName', e.target.value)}
                    className="w-full bg-[#0C0E0F] border border-[#2A2F31] rounded-xl pl-9 pr-3 py-2.5 text-white placeholder:text-gray-600 focus:outline-none focus:border-[#14B8A6] font-medium text-xs"
                  />
                </div>
              </div>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className="block text-gray-400 font-bold uppercase text-[10px] mb-1.5">
                  Business Email Address
                </label>
                <div className="relative">
                  <Mail size={15} className="absolute left-3 top-3 text-gray-500" />
                  <input
                    type="email"
                    placeholder="e.g. billing@yourbusiness.com"
                    value={formData.email || ''}
                    onChange={(e) => handleChange('email', e.target.value)}
                    className="w-full bg-[#0C0E0F] border border-[#2A2F31] rounded-xl pl-9 pr-3 py-2.5 text-white placeholder:text-gray-600 focus:outline-none focus:border-[#14B8A6] font-medium text-xs"
                  />
                </div>
              </div>

              <div>
                <label className="block text-gray-400 font-bold uppercase text-[10px] mb-1.5">
                  Business Phone Number
                </label>
                <div className="relative">
                  <Phone size={15} className="absolute left-3 top-3 text-gray-500" />
                  <input
                    type="text"
                    placeholder="e.g. +1 555 012 3456"
                    value={formData.phone || ''}
                    onChange={(e) => handleChange('phone', e.target.value)}
                    className="w-full bg-[#0C0E0F] border border-[#2A2F31] rounded-xl pl-9 pr-3 py-2.5 text-white placeholder:text-gray-600 focus:outline-none focus:border-[#14B8A6] font-medium text-xs"
                  />
                </div>
              </div>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className="block text-gray-400 font-bold uppercase text-[10px] mb-1.5">
                  Physical Office / Studio Address
                </label>
                <div className="relative">
                  <MapPin size={15} className="absolute left-3 top-3 text-gray-500" />
                  <input
                    type="text"
                    placeholder="e.g. 123 Business Street, Suite 100"
                    value={formData.address || ''}
                    onChange={(e) => handleChange('address', e.target.value)}
                    className="w-full bg-[#0C0E0F] border border-[#2A2F31] rounded-xl pl-9 pr-3 py-2.5 text-white placeholder:text-gray-600 focus:outline-none focus:border-[#14B8A6] font-medium text-xs"
                  />
                </div>
              </div>

              <div>
                <label className="block text-gray-400 font-bold uppercase text-[10px] mb-1.5">
                  Website URL
                </label>
                <div className="relative">
                  <Globe size={15} className="absolute left-3 top-3 text-gray-500" />
                  <input
                    type="text"
                    placeholder="e.g. https://yourbusiness.com"
                    value={formData.website || ''}
                    onChange={(e) => handleChange('website', e.target.value)}
                    className="w-full bg-[#0C0E0F] border border-[#2A2F31] rounded-xl pl-9 pr-3 py-2.5 text-white placeholder:text-gray-600 focus:outline-none focus:border-[#14B8A6] font-medium text-xs"
                  />
                </div>
              </div>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className="block text-gray-400 font-bold uppercase text-[10px] mb-1.5">
                  Tax Registration / Reference Number
                </label>
                <div className="relative">
                  <Shield size={15} className="absolute left-3 top-3 text-gray-500" />
                  <input
                    type="text"
                    placeholder="e.g. 401928374 or VAT-908123"
                    value={formData.taxNumber || ''}
                    onChange={(e) => handleChange('taxNumber', e.target.value)}
                    className="w-full bg-[#0C0E0F] border border-[#2A2F31] rounded-xl pl-9 pr-3 py-2.5 text-white placeholder:text-gray-600 focus:outline-none focus:border-[#14B8A6] font-medium text-xs"
                  />
                </div>
              </div>

              <div>
                <label className="block text-gray-400 font-bold uppercase text-[10px] mb-1.5 flex items-center justify-between">
                  <span>Company Registration Number</span>
                  <span className="text-[9px] text-gray-500 font-normal lowercase">(optional for sole proprietors)</span>
                </label>
                <div className="relative">
                  <Building2 size={15} className="absolute left-3 top-3 text-gray-500" />
                  <input
                    type="text"
                    placeholder="e.g. 2024/123456/07 (CIPC / Reg)"
                    value={formData.registrationNumber || ''}
                    onChange={(e) => handleChange('registrationNumber', e.target.value)}
                    className="w-full bg-[#0C0E0F] border border-[#2A2F31] rounded-xl pl-9 pr-3 py-2.5 text-white placeholder:text-gray-600 focus:outline-none focus:border-[#14B8A6] font-medium text-xs"
                  />
                </div>
              </div>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className="block text-gray-400 font-bold uppercase text-[10px] mb-1.5">
                  Social Media Handles
                </label>
                <div className="relative">
                  <Share2 size={15} className="absolute left-3 top-3 text-gray-500" />
                  <input
                    type="text"
                    placeholder="e.g. @yourbusiness"
                    value={formData.socialHandles || ''}
                    onChange={(e) => handleChange('socialHandles', e.target.value)}
                    className="w-full bg-[#0C0E0F] border border-[#2A2F31] rounded-xl pl-9 pr-3 py-2.5 text-white placeholder:text-gray-600 focus:outline-none focus:border-[#14B8A6] font-medium text-xs"
                  />
                </div>
              </div>

              <div>
                <label className="block text-gray-400 font-bold uppercase text-[10px] mb-1.5">
                  Company Logo URL
                </label>
                <div className="relative">
                  <ImageIcon size={15} className="absolute left-3 top-3 text-gray-500" />
                  <input
                    type="text"
                    placeholder="https://example.com/logo.png"
                    value={formData.logoUrl || ''}
                    onChange={(e) => handleChange('logoUrl', e.target.value)}
                    className="w-full bg-[#0C0E0F] border border-[#2A2F31] rounded-xl pl-9 pr-3 py-2.5 text-white placeholder:text-gray-600 focus:outline-none focus:border-[#14B8A6] font-medium text-xs"
                  />
                </div>
              </div>
            </div>
              {formData.logoUrl && (
                <div className="mt-3 p-3 bg-[#0C0E0F] border border-[#2A2F31] rounded-xl flex items-center gap-3">
                  <img
                    src={formData.logoUrl}
                    alt="Logo Preview"
                    className="w-12 h-12 object-contain bg-white/5 rounded-lg border border-white/10 p-1"
                    onError={(e) => { (e.target as HTMLElement).style.display = 'none'; }}
                  />
                  <div className="text-[11px] text-gray-400">
                    <span className="font-bold text-white">Logo Header Preview:</span> This logo renders at the top left of generated client invoices.
                  </div>
                </div>
              )}

            {/* APP SYSTEM VERSION & OFFLINE PWA CACHE CARD */}
            <div className="p-4 bg-[#0C0E0F] border border-[#2A2F31] rounded-2xl space-y-2 shadow-inner">
              <div className="flex items-center justify-between flex-wrap gap-3">
                <div>
                  <div className="flex items-center gap-2">
                    <h4 className="text-xs font-bold text-white">AzuraOS System Version</h4>
                    <span className="px-2 py-0.5 rounded-full bg-[#14B8A6]/20 text-[#14B8A6] text-[10px] font-bold">
                      v5.0 Stable Release
                    </span>
                  </div>
                  <p className="text-[11px] text-gray-400 mt-0.5">
                    Offline-capable Progressive Web App with auto-syncing service worker and background bundle updating.
                  </p>
                </div>
                <PWACheckUpdatesButton />
              </div>
            </div>

            {/* DELETE ACCOUNT REQUEST CARD */}
            <div className="p-4 bg-[#0C0E0F] border border-rose-500/30 rounded-2xl space-y-3 shadow-inner">
              <div className="flex items-center justify-between flex-wrap gap-3">
                <div className="flex items-center gap-2.5">
                  <div className="w-8 h-8 rounded-xl bg-rose-500/15 border border-rose-500/30 flex items-center justify-center text-rose-400 shrink-0">
                    <Trash2 size={16} />
                  </div>
                  <div>
                    <h4 className="text-xs font-bold text-white">Account &amp; Data Deletion Requests</h4>
                    <p className="text-[11px] text-gray-400">Request permanent purge of your account, business profile, and cloud vaults</p>
                  </div>
                </div>

                <button
                  type="button"
                  onClick={() => setIsDeleteModalOpen(true)}
                  className="px-4 py-2 bg-rose-500/15 hover:bg-rose-500/25 border border-rose-500/40 text-rose-300 font-bold text-xs rounded-xl transition flex items-center gap-1.5 cursor-pointer shrink-0"
                >
                  <Trash2 size={13} />
                  <span>Delete My Account and All Details</span>
                </button>
              </div>
            </div>
          </div>

          <div className="flex justify-end">
            <button
              type="submit"
              className="px-6 py-3 bg-[#14B8A6] hover:bg-[#0EA5A4] text-black font-bold text-xs rounded-xl shadow-lg shadow-[#14B8A6]/10 transition-all flex items-center gap-2 cursor-pointer"
            >
              <Save size={16} />
              <span>Save Profile Settings</span>
            </button>
          </div>
        </form>
      )}

      {/* TAB: COHORT & ACCELERATOR SUBLICENSE */}
      {activeSubTab === 'cohort_governance' && (
        <div className="space-y-6 animate-fade-in">
          <CohortGovernancePanel 
            showToast={showToast} 
            onOpenCohortOnboarding={() => setIsCohortModalOpen(true)} 
          />
        </div>
      )}

      {/* TAB 2: CURRENCY & TAX ENGINE */}
      {activeSubTab === 'currency_tax' && (
        <form onSubmit={handleSave} className="space-y-6">
          <div 
            id="global-currency-tax-engine" 
            className={`bg-[#151819] border rounded-2xl p-6 space-y-6 shadow-xl transition-all duration-500 ${
              highlightedCard === 'global-currency-tax-engine'
                ? 'border-teal-400 ring-4 ring-teal-400/30 shadow-teal-500/25 shadow-2xl'
                : 'border-[#1F2426]'
            }`}
          >
            <div className="flex items-center justify-between border-b border-[#1F2426] pb-3">
              <div className="flex items-center gap-2 text-white font-bold text-sm">
                <DollarSign size={18} className="text-[#14B8A6]" />
                <span>Global Currency & Country Tax Rules Engine</span>
              </div>
              <span className="px-2.5 py-0.5 rounded-md bg-teal-500/10 border border-teal-500/30 text-teal-300 text-[10px] font-mono font-bold">
                Global Pilot Ready
              </span>
            </div>

            {/* Regional Jurisdiction Selection Grid */}
            <div className="p-4 bg-[#0C0E0F] border border-[#2A2F31] rounded-2xl space-y-3">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2 text-white font-bold text-xs">
                  <Globe size={15} className="text-[#14B8A6]" />
                  <span>1-Click Regional Jurisdiction Preset</span>
                </div>
                <span className="text-[10px] text-gray-500">Auto-configures currency, tax rates &amp; statutory bodies</span>
              </div>

              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-2">
                {JURISDICTION_LIST.map((j) => {
                  const isSelected = (formData.jurisdiction || 'ZA') === j.id;
                  return (
                    <button
                      key={j.id}
                      type="button"
                      onClick={() => {
                        setFormData(prev => ({
                          ...prev,
                          jurisdiction: j.id,
                          currencySymbol: j.currencySymbol,
                          currencyCode: j.currencyCode,
                          vatRate: j.vatRate,
                          taxAuthorityLabel: j.taxAuthorityLabel,
                          taxNumberLabel: j.taxNumberLabel,
                          invoiceTypeLabel: j.invoiceTypeLabel,
                          fiscalYearEndMonth: j.fiscalYearEndMonth
                        }));
                      }}
                      className={`p-3 rounded-xl border text-left transition-all cursor-pointer flex flex-col justify-between gap-1.5 ${
                        isSelected
                          ? 'bg-[#14B8A6]/15 border-[#14B8A6] text-white shadow-sm ring-1 ring-[#14B8A6]/30'
                          : 'bg-[#151819] border-[#1F2426] text-gray-300 hover:border-[#2A2F31] hover:text-white'
                      }`}
                    >
                      <div className="flex items-center justify-between">
                        <span className="text-xl">{j.flag}</span>
                        {isSelected && <Check size={13} className="text-[#14B8A6]" />}
                      </div>
                      <div>
                        <div className="font-bold text-xs leading-tight truncate">{j.name}</div>
                        <div className="text-[10px] text-gray-500 font-mono mt-0.5">{j.currencyCode} ({j.currencySymbol}) &bull; {j.vatRate}%</div>
                      </div>
                    </button>
                  );
                })}
              </div>

              <div className="p-2.5 bg-[#151819] border border-[#1F2426] rounded-xl flex items-center justify-between text-[11px] text-gray-300">
                <span className="flex items-center gap-1.5 font-medium">
                  <span className="w-1.5 h-1.5 rounded-full bg-teal-400" />
                  Active Preset: <strong className="text-white">{getJurisdictionPreset(formData.jurisdiction).name}</strong> — {getJurisdictionPreset(formData.jurisdiction).description}
                </span>
              </div>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
              <div>
                <label className="block text-gray-400 font-bold uppercase text-[10px] mb-1.5">
                  Operating Currency
                </label>
                <select
                  value={`${formData.currencySymbol || 'R'}|${formData.currencyCode || 'ZAR'}`}
                  onChange={(e) => {
                    const [sym, code] = e.target.value.split('|');
                    setFormData(prev => ({ ...prev, currencySymbol: sym, currencyCode: code }));
                  }}
                  className="w-full bg-[#0C0E0F] border border-[#2A2F31] rounded-xl px-3 py-2.5 text-white font-medium text-xs focus:outline-none focus:border-[#14B8A6]"
                >
                  <option value="R|ZAR">ZAR (R - South African Rand)</option>
                  <option value="$|USD">USD ($ - US Dollar)</option>
                  <option value="€|EUR">EUR (€ - Euro)</option>
                  <option value="£|GBP">GBP (£ - British Pound)</option>
                  <option value="₹|INR">INR (₹ - Indian Rupee)</option>
                  <option value="A$|AUD">AUD (A$ - Australian Dollar)</option>
                  <option value="C$|CAD">CAD (C$ - Canadian Dollar)</option>
                  <option value="Fr|CHF">CHF (Fr - Swiss Franc)</option>
                  <option value="AED|AED">AED (Emirati Dirham)</option>
                </select>
              </div>

              <div>
                <label className="block text-gray-400 font-bold uppercase text-[10px] mb-1.5">
                  Applied VAT / GST Rate (%)
                </label>
                <div className="relative flex items-center">
                  <input
                    type="number"
                    step="0.1"
                    min="0"
                    max="50"
                    placeholder="15"
                    value={formData.vatRate !== undefined ? formData.vatRate : 15}
                    onChange={(e) => setFormData(prev => ({ ...prev, vatRate: parseFloat(e.target.value) || 0 }))}
                    className="w-full bg-[#0C0E0F] border border-[#2A2F31] rounded-xl px-3 py-2.5 text-white font-medium text-xs focus:outline-none focus:border-[#14B8A6]"
                  />
                  <span className="absolute right-3 text-gray-400 font-bold text-xs">%</span>
                </div>
              </div>

              <div>
                <label className="block text-gray-400 font-bold uppercase text-[10px] mb-1.5">
                  Tax Authority / Dept Label
                </label>
                <input
                  type="text"
                  placeholder="e.g. SARS, IRS, HMRC, GST Dept"
                  value={formData.taxAuthorityLabel || ''}
                  onChange={(e) => handleChange('taxAuthorityLabel', e.target.value)}
                  className="w-full bg-[#0C0E0F] border border-[#2A2F31] rounded-xl px-3 py-2.5 text-white placeholder:text-gray-600 focus:outline-none focus:border-[#14B8A6] font-medium text-xs"
                />
              </div>

              <div>
                <label className="block text-gray-400 font-bold uppercase text-[10px] mb-1.5">
                  Fiscal Year-End Month
                </label>
                <select
                  value={formData.fiscalYearEndMonth || 2}
                  onChange={(e) => handleChange('fiscalYearEndMonth', parseInt(e.target.value, 10))}
                  className="w-full bg-[#0C0E0F] border border-[#2A2F31] rounded-xl px-3 py-2.5 text-white font-medium text-xs focus:outline-none focus:border-[#14B8A6]"
                >
                  <option value="1">January 31</option>
                  <option value="2">February 28/29 (South Africa, Kenya)</option>
                  <option value="3">March 31 (UK, India, Japan, Canada)</option>
                  <option value="4">April 30</option>
                  <option value="5">May 31</option>
                  <option value="6">June 30 (Australia, Egypt, NZ)</option>
                  <option value="7">July 31</option>
                  <option value="8">August 31</option>
                  <option value="9">September 30 (US Federal / Corp)</option>
                  <option value="10">October 31</option>
                  <option value="11">November 30</option>
                  <option value="12">December 31 (US, EU, Standard)</option>
                </select>
              </div>
            </div>

            {/* Formula Explanation Card */}
            <div className="p-4 bg-[#0C0E0F] border border-[#2A2F31] rounded-xl space-y-3">
              <h4 className="text-xs font-bold text-white uppercase tracking-wider flex items-center gap-1.5">
                <Receipt size={14} className="text-amber-400" />
                Automatic Tax Filing Formula Preview ({currentVatRate}%)
              </h4>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-xs">
                <div className="p-3 bg-[#151819] border border-[#1F2426] rounded-xl space-y-1">
                  <span className="text-gray-400 font-bold text-[10px] uppercase">Output Tax (Revenue Collected)</span>
                  <p className="text-white font-mono font-bold">
                    Net Revenue = Gross Revenue / (1 + {currentVatRate / 100})
                  </p>
                  <p className="text-emerald-400 text-[11px]">
                    Output Tax = Gross Revenue - Net Revenue
                  </p>
                </div>

                <div className="p-3 bg-[#151819] border border-[#1F2426] rounded-xl space-y-1">
                  <span className="text-gray-400 font-bold text-[10px] uppercase">Input Tax (Expenses Claimable)</span>
                  <p className="text-white font-mono font-bold">
                    Net Expense = Gross Expense / (1 + {currentVatRate / 100})
                  </p>
                  <p className="text-rose-400 text-[11px]">
                    Input Tax = Gross Expense - Net Expense
                  </p>
                </div>
              </div>
            </div>

            {/* DELETE ACCOUNT REQUEST CARD */}
            <div className="p-4 bg-[#0C0E0F] border border-rose-500/30 rounded-2xl space-y-3 shadow-inner">
              <div className="flex items-center justify-between flex-wrap gap-3">
                <div className="flex items-center gap-2.5">
                  <div className="w-8 h-8 rounded-xl bg-rose-500/15 border border-rose-500/30 flex items-center justify-center text-rose-400 shrink-0">
                    <Trash2 size={16} />
                  </div>
                  <div>
                    <h4 className="text-xs font-bold text-white">Account &amp; Data Deletion Requests</h4>
                    <p className="text-[11px] text-gray-400">Request permanent purge of your account, business profile, and cloud vaults</p>
                  </div>
                </div>

                <button
                  type="button"
                  onClick={() => setIsDeleteModalOpen(true)}
                  className="px-4 py-2 bg-rose-500/15 hover:bg-rose-500/25 border border-rose-500/40 text-rose-300 font-bold text-xs rounded-xl transition flex items-center gap-1.5 cursor-pointer shrink-0"
                >
                  <Trash2 size={13} />
                  <span>Delete My Account and All Details</span>
                </button>
              </div>
            </div>
          </div>

          <div className="flex justify-end">
            <button
              type="submit"
              className="px-6 py-3 bg-[#14B8A6] hover:bg-[#0EA5A4] text-black font-bold text-xs rounded-xl shadow-lg shadow-[#14B8A6]/10 transition-all flex items-center gap-2 cursor-pointer"
            >
              <Save size={16} />
              <span>Save Currency & Tax Rules</span>
            </button>
          </div>
        </form>
      )}

      {/* TAB 3: INVOICING & PAYMENT GATEWAYS */}
      {activeSubTab === 'payments' && (
        <form onSubmit={handleSave} className="space-y-6">
          <div 
            id="invoice-payment-gateways" 
            className={`bg-[#151819] border rounded-2xl p-6 space-y-6 shadow-xl transition-all duration-500 ${
              highlightedCard === 'invoice-payment-gateways'
                ? 'border-purple-400 ring-4 ring-purple-400/30 shadow-purple-500/25 shadow-2xl'
                : 'border-[#1F2426]'
            }`}
          >
            <div className="flex items-center justify-between border-b border-[#1F2426] pb-3">
              <div className="flex items-center gap-2 text-white font-bold text-sm">
                <CreditCard size={18} className="text-[#14B8A6]" />
                <span>Receivables, Payment Links & Wire Instructions</span>
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {/* Left Column: Input Controls */}
              <div className="space-y-4">
                <div>
                  <label className="block text-gray-400 font-bold uppercase text-[10px] mb-1.5 flex items-center justify-between">
                    <span>Live Online Payment Link</span>
                    <span className="text-teal-400 text-[9px] font-normal">Stripe, PayFast, Yoco, PayPal, Wise, Revolut</span>
                  </label>
                  <div className="relative">
                    <Link2 size={15} className="absolute left-3 top-3 text-teal-400" />
                    <input
                      type="url"
                      placeholder="https://buy.stripe.com/live_link or https://payfast.co.za/..."
                      value={formData.paymentLink || ''}
                      onChange={(e) => handleChange('paymentLink', e.target.value)}
                      className="w-full bg-[#0C0E0F] border border-[#2A2F31] rounded-xl pl-9 pr-3 py-2.5 text-white placeholder:text-gray-600 focus:outline-none focus:border-[#14B8A6] font-mono text-xs"
                    />
                  </div>
                  <p className="text-[10px] text-gray-400 mt-1">
                    Adds a prominent <strong>"Pay Invoice Online"</strong> direct checkout button to client PDF invoices.
                  </p>
                </div>

                <div>
                  <label className="block text-gray-400 font-bold uppercase text-[10px] mb-1.5">
                    Direct Bank Account Wire Details
                  </label>
                  <textarea
                    rows={4}
                    placeholder="Bank: Your Bank Name&#10;Account Name: Your Business Name&#10;Account #: 1234567890&#10;Branch Code: 000000&#10;SWIFT/IBAN: XXXX..."
                    value={formData.bankDetails || ''}
                    onChange={(e) => handleChange('bankDetails', e.target.value)}
                    className="w-full bg-[#0C0E0F] border border-[#2A2F31] rounded-xl p-3 text-white placeholder:text-gray-600 focus:outline-none focus:border-[#14B8A6] font-mono text-xs"
                  />
                  <p className="text-[10px] text-gray-400 mt-1">
                    Formally formatted at the bottom of generated invoices for direct EFT/Wire payments.
                  </p>
                </div>
              </div>

              {/* Right Column: Live Client Invoice Rendering Preview */}
              <div className="p-3.5 sm:p-4 bg-[#0C0E0F] border border-[#2A2F31] rounded-2xl space-y-3 min-w-0 max-w-full overflow-hidden">
                <div className="flex items-center justify-between text-xs gap-2">
                  <span className="font-bold text-white uppercase text-[10px] tracking-wider flex items-center gap-1.5 truncate">
                    <FileText size={14} className="text-[#14B8A6] shrink-0" /> <span className="truncate">Live Client Invoice Rendering Preview</span>
                  </span>
                  <span className="text-gray-400 text-[10px] font-mono shrink-0">{currentCode}</span>
                </div>

                <div className="p-3 sm:p-4 bg-white text-slate-900 rounded-xl shadow-md space-y-3 font-sans text-xs min-w-0 max-w-full overflow-hidden break-words">
                  <div className="flex flex-col sm:flex-row justify-between items-start gap-2 border-b border-slate-200 pb-2.5 min-w-0">
                    <div className="min-w-0 max-w-full pr-1 space-y-0.5">
                      <div className="font-bold text-xs sm:text-sm text-slate-900 leading-snug break-words">
                        {formData.companyName || formData.senderName || 'Your Business Name'}
                      </div>
                      <div className="text-[10px] text-slate-500 break-all">
                        {formData.email || 'billing@yourbusiness.com'}
                      </div>
                      {formData.taxNumber && (
                        <div className="text-[9px] text-slate-400 break-words">VAT Reg: {formData.taxNumber}</div>
                      )}
                      {formData.registrationNumber && (
                        <div className="text-[9px] text-slate-400 break-words">Company Reg: {formData.registrationNumber}</div>
                      )}
                    </div>
                    <div className="text-left sm:text-right shrink-0 min-w-0">
                      <div className="font-extrabold text-teal-700 text-xs sm:text-xs tracking-tight">INVOICE #INV-2026</div>
                      <div className="text-[9px] text-slate-400">Rate: {currentVatRate}% VAT Included</div>
                    </div>
                  </div>

                  <div className="py-2 border-b border-slate-100 flex justify-between items-center gap-2 text-[11px] min-w-0">
                    <div className="min-w-0 pr-1">
                      <div className="font-semibold text-slate-800 break-words">Retainer Consultancy Service</div>
                      <div className="text-[9px] text-slate-500">Gross Price Incl. {currentVatRate}% Tax</div>
                    </div>
                    <div className="font-bold font-mono text-slate-900 shrink-0">{currentCurrency}1,500.00</div>
                  </div>

                  {formData.paymentLink && (
                    <div className="p-2 bg-teal-50 border border-teal-200 rounded-lg flex flex-col sm:flex-row items-start sm:items-center justify-between gap-1.5 min-w-0">
                      <span className="text-[10px] font-bold text-teal-900 break-words">Instant Online Payment Gateway:</span>
                      <a
                        href={formData.paymentLink}
                        target="_blank"
                        rel="noreferrer"
                        className="px-2.5 py-1 bg-teal-600 text-white rounded text-[10px] font-bold flex items-center gap-1 hover:bg-teal-700 shrink-0"
                      >
                        <span>Pay Online Now</span>
                        <ExternalLink size={10} />
                      </a>
                    </div>
                  )}

                  {formData.bankDetails && (
                    <div className="p-2 bg-slate-50 border border-slate-200 rounded-lg text-[9px] font-mono text-slate-700 whitespace-pre-line break-words max-w-full overflow-hidden">
                      <div className="font-bold text-slate-900 mb-0.5">EFT / Wire Transfer Details:</div>
                      {formData.bankDetails}
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>

          <div className="flex justify-end pt-2">
            <button
              type="submit"
              className="w-full sm:w-auto px-6 py-3 bg-[#14B8A6] hover:bg-[#0EA5A4] text-black font-bold text-xs rounded-xl shadow-lg shadow-[#14B8A6]/10 transition-all flex items-center justify-center gap-2 cursor-pointer"
            >
              <Save size={16} />
              <span>Save Invoicing Settings</span>
            </button>
          </div>
        </form>
      )}

      {/* TAB 4: SUBSCRIPTION & BILLING */}
      {activeSubTab === 'billing' && (
        <SubscriptionBillingView
          showToast={showToast}
          companyName={formData.companyName || formData.senderName}
          onRefreshAuditLogs={() => setAuditLogs(getAuditLogs())}
          onNavigate={onNavigate}
        />
      )}

      {/* TAB 5: DATA EXPORTS & TAX FILING CENTER */}
      {activeSubTab === 'exports' && (
        <div className="space-y-6">
          <div className="bg-[#151819] border border-[#1F2426] rounded-2xl p-6 shadow-xl">
            <ExportCenter
              bookings={bookings}
              expenses={expenses}
              insights={insights}
              streams={streams}
              threshold={threshold}
              onOpenReconciler={onOpenReconciler}
            />
          </div>
        </div>
      )}

      {/* TAB: ACCOUNTANT & CPA AUDIT VIEW */}
      {activeSubTab === 'accountant_view' && (
        <div className="space-y-6">
          <AccountantAuditView
            bookings={bookings}
            expenses={expenses}
            streams={streams}
            settings={formData}
            showToast={showToast}
          />
        </div>
      )}

      {/* TAB: SECURITY & AES-256 VAULT SAFEGUARDS */}
      {activeSubTab === 'security_safeguards' && (
        <SecuritySafeguardsCard
          onRefreshLogs={() => setAuditLogs(getAuditLogs())}
          showToast={showToast}
        />
      )}

      {/* TAB 6: DATABASE AUDIT LOG & TRANSPARENCY TRAIL */}
      {activeSubTab === 'audit_log' && (
        <div className="space-y-6">
          {/* Audit Trail Banner */}
          <div className="p-6 bg-gradient-to-r from-purple-500/10 via-[#151819] to-purple-500/5 border border-purple-500/30 rounded-2xl shadow-xl space-y-4">
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
              <div className="flex items-start gap-3">
                <div className="w-10 h-10 rounded-xl bg-purple-500/20 border border-purple-500/40 text-purple-400 flex items-center justify-center shrink-0 shadow-inner">
                  <History size={20} />
                </div>
                <div>
                  <div className="flex items-center gap-2">
                    <h2 className="text-lg font-bold text-white tracking-tight">Database Audit Trail & Activity Log</h2>
                    <span className="px-2 py-0.5 text-[9px] font-extrabold uppercase bg-purple-500/20 text-purple-300 rounded border border-purple-500/40">
                      LIVE TRANSPARENCY
                    </span>
                  </div>
                  <p className="text-xs text-gray-400 mt-1 leading-relaxed max-w-2xl">
                    Time-stamped event history tracking every critical database modification (bookings created/modified, expense entries, strategic AI insight generation, system reloads, and access changes) to ensure maximum transparency for the workspace owner.
                  </p>
                </div>
              </div>

              <div className="flex items-center gap-2 shrink-0 flex-wrap sm:flex-nowrap">
                <button
                  onClick={() => {
                    setAuditLogs(getAuditLogs());
                    showToast('🔄 Audit trail refreshed.');
                  }}
                  className="px-3.5 py-2 bg-[#1F2426] hover:bg-[#2A2F31] text-gray-300 hover:text-white font-bold text-xs rounded-xl border border-[#2A2F31] transition flex items-center gap-2 cursor-pointer"
                >
                  <RefreshCw size={13} />
                  <span>Refresh Log</span>
                </button>

                <button
                  onClick={handleExportAuditLogsCSV}
                  className="px-3.5 py-2 bg-purple-500 hover:bg-purple-400 text-black font-extrabold text-xs rounded-xl shadow-md transition flex items-center gap-2 cursor-pointer"
                >
                  <Download size={13} />
                  <span>Export CSV</span>
                </button>
              </div>
            </div>

            {/* Quick Metrics */}
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 pt-2">
              <div className="p-3 bg-[#0C0E0F]/80 border border-[#1F2426] rounded-xl">
                <div className="text-[10px] text-gray-400 font-bold uppercase tracking-wider">Total Audited Events</div>
                <div className="text-lg font-extrabold text-purple-400 font-mono mt-0.5">{auditLogs.length} Entries</div>
              </div>

              <div className="p-3 bg-[#0C0E0F]/80 border border-[#1F2426] rounded-xl">
                <div className="text-[10px] text-gray-400 font-bold uppercase tracking-wider">Bookings & Expenses</div>
                <div className="text-lg font-extrabold text-[#14B8A6] font-mono mt-0.5">
                  {auditLogs.filter(l => l.entityType === 'Booking' || l.entityType === 'Expense').length} Actions
                </div>
              </div>

              <div className="p-3 bg-[#0C0E0F]/80 border border-[#1F2426] rounded-xl">
                <div className="text-[10px] text-gray-400 font-bold uppercase tracking-wider">Strategic Insights</div>
                <div className="text-lg font-extrabold text-amber-400 font-mono mt-0.5">
                  {auditLogs.filter(l => l.entityType === 'Insight').length} Created
                </div>
              </div>

              <div className="p-3 bg-[#0C0E0F]/80 border border-[#1F2426] rounded-xl">
                <div className="text-[10px] text-gray-400 font-bold uppercase tracking-wider">Security & System</div>
                <div className="text-lg font-extrabold text-blue-400 font-mono mt-0.5">
                  {auditLogs.filter(l => l.entityType === 'Security' || l.entityType === 'System' || l.entityType === 'Settings').length} Events
                </div>
              </div>
            </div>
          </div>

          {/* AUDIT LOG FILTERING & TIMELINE CONTAINER */}
          <div className="p-6 bg-[#151819] border border-[#1F2426] rounded-2xl shadow-xl space-y-6">
            {/* Toolbar: Search + Category Filters + Clear Button */}
            <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4">
              {/* Category Pills */}
              <div className="flex items-center gap-1.5 overflow-x-auto pb-1 max-w-full">
                {[
                  { id: 'all', label: 'All Events' },
                  { id: 'Booking', label: 'Bookings' },
                  { id: 'Expense', label: 'Expenses' },
                  { id: 'Insight', label: 'Insights' },
                  { id: 'Security', label: 'Security' },
                  { id: 'System', label: 'System' }
                ].map((cat) => (
                  <button
                    key={cat.id}
                    onClick={() => setAuditFilterCategory(cat.id)}
                    className={`px-3 py-1.5 rounded-xl text-xs font-bold transition-all whitespace-nowrap cursor-pointer ${
                      auditFilterCategory === cat.id
                        ? 'bg-purple-500/20 text-purple-300 border border-purple-500/40 shadow-sm'
                        : 'bg-[#0C0E0F] text-gray-400 hover:text-white border border-[#2A2F31]'
                    }`}
                  >
                    {cat.label}
                  </button>
                ))}
              </div>

              {/* Search + Clear Action */}
              <div className="flex items-center gap-2 w-full lg:w-auto">
                <div className="relative w-full lg:w-64">
                  <Search size={14} className="absolute left-3 top-3 text-gray-500" />
                  <input
                    type="text"
                    placeholder="Search logs by keyword..."
                    value={auditSearchQuery}
                    onChange={(e) => setAuditSearchQuery(e.target.value)}
                    className="w-full bg-[#0C0E0F] border border-[#2A2F31] rounded-xl pl-8 pr-3 py-2 text-xs text-white placeholder:text-gray-500 focus:outline-none focus:border-purple-500 font-medium"
                  />
                </div>

                {auditLogs.length > 0 && (
                  <button
                    onClick={handleClearAuditLogsHistory}
                    className="px-3 py-2 bg-rose-500/10 hover:bg-rose-500/20 text-rose-400 border border-rose-500/20 rounded-xl text-xs font-bold transition flex items-center gap-1.5 shrink-0 cursor-pointer"
                    title="Clear audit log history"
                  >
                    <Trash2 size={13} />
                    <span className="hidden sm:inline">Clear Log</span>
                  </button>
                )}
              </div>
            </div>

            {/* TIMELINE EVENT LIST */}
            <div className="space-y-3 relative before:absolute before:left-6 before:top-4 before:bottom-4 before:w-0.5 before:bg-[#2A2F31] before:hidden sm:before:block">
              {auditLogs
                .filter((log) => {
                  const matchesCat = auditFilterCategory === 'all' || log.entityType === auditFilterCategory;
                  const matchesSearch =
                    !auditSearchQuery.trim() ||
                    log.details.toLowerCase().includes(auditSearchQuery.toLowerCase()) ||
                    log.actionType.toLowerCase().includes(auditSearchQuery.toLowerCase()) ||
                    log.performedBy.toLowerCase().includes(auditSearchQuery.toLowerCase()) ||
                    (log.entityId && log.entityId.toLowerCase().includes(auditSearchQuery.toLowerCase()));
                  return matchesCat && matchesSearch;
                })
                .map((log) => {
                  // Determine visual styling by entityType
                  let icon = <Database size={15} />;
                  let colorClasses = 'bg-blue-500/20 text-blue-400 border-blue-500/30';
                  let badgeText = log.entityType.toUpperCase();

                  if (log.entityType === 'Booking') {
                    icon = <Calendar size={15} />;
                    colorClasses = 'bg-[#14B8A6]/20 text-[#14B8A6] border-[#14B8A6]/30';
                  } else if (log.entityType === 'Expense') {
                    icon = <DollarSign size={15} />;
                    colorClasses = 'bg-rose-500/20 text-rose-400 border-rose-500/30';
                  } else if (log.entityType === 'Insight') {
                    icon = <Lightbulb size={15} />;
                    colorClasses = 'bg-purple-500/20 text-purple-400 border-purple-500/30';
                  } else if (log.entityType === 'Stream') {
                    icon = <Layers size={15} />;
                    colorClasses = 'bg-sky-500/20 text-sky-400 border-sky-500/30';
                  } else if (log.entityType === 'Security') {
                    icon = <Shield size={15} />;
                    colorClasses = 'bg-amber-500/20 text-amber-400 border-amber-500/30';
                  } else if (log.entityType === 'Settings') {
                    icon = <Building2 size={15} />;
                    colorClasses = 'bg-emerald-500/20 text-emerald-400 border-emerald-500/30';
                  }

                  const formattedDate = new Date(log.timestamp).toLocaleDateString([], {
                    month: 'short',
                    day: 'numeric',
                    year: 'numeric'
                  });
                  const formattedTime = new Date(log.timestamp).toLocaleTimeString([], {
                    hour: '2-digit',
                    minute: '2-digit',
                    second: '2-digit'
                  });

                  return (
                    <div
                      key={log.id}
                      className="p-4 bg-[#0C0E0F] border border-[#2A2F31] rounded-xl relative sm:ml-12 hover:border-gray-700 transition space-y-2"
                    >
                      {/* Left timeline node icon for desktop */}
                      <div className={`hidden sm:flex absolute -left-12 top-4 w-8 h-8 rounded-lg border items-center justify-center shrink-0 shadow-md ${colorClasses}`}>
                        {icon}
                      </div>

                      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2">
                        <div className="flex items-center gap-2 flex-wrap">
                          <span className={`px-2 py-0.5 text-[9px] font-extrabold uppercase rounded border ${colorClasses}`}>
                            {badgeText}
                          </span>
                          <span className="px-2 py-0.5 text-[9px] font-extrabold font-mono bg-[#151819] text-gray-300 rounded border border-[#2A2F31]">
                            {log.actionType}
                          </span>
                          {log.entityId && (
                            <span className="px-1.5 py-0.5 text-[9px] font-mono text-gray-500 bg-[#151819] rounded border border-[#2A2F31]">
                              ID: {log.entityId}
                            </span>
                          )}
                        </div>

                        <div className="flex items-center gap-2 text-[11px] text-gray-400 font-mono">
                          <Clock size={12} className="text-gray-500" />
                          <span>{formattedDate} • {formattedTime}</span>
                        </div>
                      </div>

                      {/* Log Action Details */}
                      <p className="text-xs text-gray-200 font-medium leading-relaxed pl-0.5">
                        {log.details}
                      </p>

                      <div className="flex items-center justify-between pt-1 border-t border-[#1F2426]/60 text-[10px] text-gray-500">
                        <span>Performed by: <strong className="text-gray-300 font-mono">{log.performedBy}</strong></span>
                        <span className="font-mono text-gray-600">{log.id}</span>
                      </div>
                    </div>
                  );
                })}

              {auditLogs.filter((log) => {
                const matchesCat = auditFilterCategory === 'all' || log.entityType === auditFilterCategory;
                const matchesSearch =
                  !auditSearchQuery.trim() ||
                  log.details.toLowerCase().includes(auditSearchQuery.toLowerCase()) ||
                  log.actionType.toLowerCase().includes(auditSearchQuery.toLowerCase()) ||
                  log.performedBy.toLowerCase().includes(auditSearchQuery.toLowerCase());
                return matchesCat && matchesSearch;
              }).length === 0 && (
                <div className="p-12 text-center bg-[#0C0E0F] border border-dashed border-[#2A2F31] rounded-2xl space-y-2">
                  <History size={24} className="mx-auto text-purple-400/60 mb-2" />
                  <p className="font-bold text-gray-300 text-sm">No audit logs matching criteria.</p>
                  <p className="text-xs text-gray-500 max-w-md mx-auto">
                    New database mutations (creating bookings, logging expenses, generating AI insights, or updating settings) will automatically append to this transparency timeline.
                  </p>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* TAB 8: SUPER-ADMIN EXECUTIVE CONSOLE (EXCLUSIVELY FOR iwillriseupstudio@gmail.com) */}
      {activeSubTab === 'super_admin' && (
        isSuperAdmin ? (
          <SuperAdminTabContent
            currentUser={currentUser}
            showToast={showToast}
            onNavigate={onNavigate}
          />
        ) : (
          <div className="p-8 bg-[#151819] border border-rose-500/30 rounded-2xl text-center space-y-4 max-w-lg mx-auto my-12">
            <div className="w-12 h-12 bg-rose-500/10 rounded-full flex items-center justify-center text-rose-400 mx-auto">
              <Lock size={24} />
            </div>
            <h3 className="text-lg font-bold text-white">Super-Admin Authorization Required</h3>
            <p className="text-xs text-gray-400">
              This executive panel is restricted exclusively to authorized master administrators.
            </p>
            <button
              onClick={() => setActiveSubTab('profile')}
              className="px-4 py-2 bg-[#1F2426] text-white rounded-xl text-xs font-bold hover:bg-[#2A2F31] transition"
            >
              Return to Profile Settings
            </button>
          </div>
        )
      )}

      {/* Account Deletion Request Confirmation Modal */}
      <DeleteAccountModal
        isOpen={isDeleteModalOpen}
        onClose={() => setIsDeleteModalOpen(false)}
        bookings={bookings}
        expenses={expenses}
        insights={insights}
        streams={streams}
        onOpenExportCenter={() => setActiveSubTab('exports')}
        showToast={showToast}
      />

      {/* Cohort Onboarding & Sublicense Voucher Modal */}
      <CohortOnboardingModal
        isOpen={isCohortModalOpen}
        onClose={() => setIsCohortModalOpen(false)}
        onActivated={(newSettings) => {
          setIsCohortModalOpen(false);
          showToast(`🎓 ${newSettings.cohortName} sponsorship activated!`);
        }}
        showToast={showToast}
      />
    </div>
  );
}

export const BusinessHQView = React.memo(BusinessHQViewComponent);
export default BusinessHQView;

